import React, { useState } from 'react';
import './EarningsCalculator.css';

const EarningsCalculator = () => {
  const [hours, setHours] = useState(8);

  const calculateEarnings = (hours) => {
    // Fixed earnings: Up to ₹14 per hour
    const fixedEarningsPerHour = 14;
    const fixedEarnings = Math.round(fixedEarningsPerHour * hours * 30);
    
    // Call-based earnings (when taking calls proactively)
    // Call rates: 0-1min=₹0.60, 1-3min=₹1.20, 3-5min=₹1.50, 5min+=₹2.00
    // Proactive users take more calls and longer calls
    const callsPerHourMin = 10; // Minimum calls per hour
    const callsPerHourMax = 25; // Proactive users can take 20-25 calls/hour
    const avgCallEarningMin = 1.20; // Average for shorter calls
    const avgCallEarningMax = 1.75; // Average for proactive users (mix of longer calls)
    
    const callEarningsMin = Math.round(callsPerHourMin * avgCallEarningMin * hours * 30);
    let callEarningsMax = Math.round(callsPerHourMax * avgCallEarningMax * hours * 30);
    
    // Gifting earnings commission: Up to ₹3000 per week = ₹12,000 per month (max)
    // Estimated based on hours worked (higher hours = higher potential)
    const giftingMaxMonthly = 12000; // ₹3000/week × 4 weeks
    const giftingPercentage = Math.min(0.25 + (hours - 4) * 0.075, 1); // 25% at 4h, scaling up to 100% at 12h+
    const giftingMin = Math.round(giftingMaxMonthly * giftingPercentage * 0.5);
    const giftingMax = Math.round(giftingMaxMonthly * giftingPercentage);
    
    // Chat messages commission: Up to ₹3000 per week = ₹12,000 per month (max)
    const chatMaxMonthly = 12000; // ₹3000/week × 4 weeks
    const chatPercentage = Math.min(0.25 + (hours - 4) * 0.075, 1); // Same scaling as gifting
    const chatMin = Math.round(chatMaxMonthly * chatPercentage * 0.5);
    const chatMax = Math.round(chatMaxMonthly * chatPercentage);
    
    // Total calculations
    // Minimum: Fixed + minimum calls + minimum commissions
    const totalMin = fixedEarnings + callEarningsMin + giftingMin + chatMin;
    // Maximum: Fixed + maximum calls (proactive) + maximum commissions
    // For 8-12 hours, should reach ₹50-60K for very proactive users
    let totalMax = fixedEarnings + callEarningsMax + giftingMax + chatMax;
    
    // Ensure max reaches ₹50-60K range for 8+ hours when proactive
    if (hours >= 8) {
      const targetMax = hours >= 12 ? 60000 : 50000;
      if (totalMax < targetMax) {
        // Adjust call earnings to reach target for proactive users
        const additionalNeeded = targetMax - totalMax;
        totalMax = targetMax;
        callEarningsMax = callEarningsMax + additionalNeeded;
      }
    }
    
    return { 
      fixedEarnings, 
      callEarningsMin,
      callEarningsMax,
      giftingMin, 
      giftingMax, 
      chatMin, 
      chatMax, 
      totalMin, 
      totalMax 
    };
  };

  const earnings = calculateEarnings(hours);

  return (
    <section className="earnings-calculator">
      <div className="container">
        <div className="calculator-card">
          <h2 className="calculator-title">Calculate Your Potential Earnings</h2>
          <p className="calculator-subtitle">
            See how much you could earn as a Hopmate partner based on your daily hours
          </p>
          
          <div className="slider-container">
            <div className="slider-labels">
              <span>4 hours</span>
              <span>8 hours</span>
              <span>12 hours</span>
            </div>
            <input
              type="range"
              min="4"
              max="12"
              step="2"
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              className="hours-slider"
            />
            <div className="slider-value">{hours} hours online daily</div>
          </div>

          <div className="earnings-breakdown">
            <div className="breakdown-header">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10 5V10L12.5 12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <span>With {hours} Hours Daily</span>
            </div>
            <div className="earnings-list">
              <div className="earnings-item">
                <span className="earnings-label">Fixed Earnings (Voice Room Hosting)</span>
                <span className="earnings-amount">₹{earnings.fixedEarnings.toLocaleString('en-IN')}</span>
              </div>
              <div className="earnings-item">
                <span className="earnings-label">Call-Based Earnings</span>
                <span className="earnings-amount">
                  ₹{earnings.callEarningsMin.toLocaleString('en-IN')} - ₹{earnings.callEarningsMax.toLocaleString('en-IN')}
                </span>
              </div>
              <div className="earnings-item">
                <span className="earnings-label">Virtual Gifts Commission</span>
                <span className="earnings-amount">
                  ₹{earnings.giftingMin.toLocaleString('en-IN')} - ₹{earnings.giftingMax.toLocaleString('en-IN')}
                </span>
              </div>
              <div className="earnings-item">
                <span className="earnings-label">Chat Messages Commission</span>
                <span className="earnings-amount">
                  ₹{earnings.chatMin.toLocaleString('en-IN')} - ₹{earnings.chatMax.toLocaleString('en-IN')}
                </span>
              </div>
              <div className="earnings-item total">
                <span className="earnings-label">Total Monthly Earnings</span>
                <span className="earnings-amount highlight">
                  ₹{earnings.totalMin.toLocaleString('en-IN')} - ₹{earnings.totalMax.toLocaleString('en-IN')}
                </span>
              </div>
            </div>
            <p className="earnings-disclaimer">
              *Earnings vary based on content quality, engagement, and consistency. Fixed earnings up to ₹14/hour. Call earnings based on duration (0-1min: ₹0.60, 1-3min: ₹1.20, 3-5min: ₹1.50, 5min+: ₹2.00). Proactive users taking calls regularly can earn ₹50,000-₹60,000 monthly. Gifting and chat commissions can go up to ₹3,000 per week each.
            </p>
          </div>

          <a 
            href="https://forms.gle/BqwhQXFZpfiouwds9" 
            target="_blank" 
            rel="noopener noreferrer"
            className="calculator-cta-button"
          >
            Apply Now to Start Earning
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default EarningsCalculator;

